import { removePendingAssetJobIds } from '@/lib/stores/assetUploadPendings';
import React from 'react';
import { classNames } from '~/utils/classNames';
import { extractJobIdFromObjectName } from '~/lib/utils/assetClientUpload';

interface AttachedImage {
  id: string;
  base64: string;
  isAttaching?: boolean;
  source?: 'figma' | 'upload' | 'paste' | 'asset' | 'prompt';
  jobId?: string;
  gcsURL?: string;
  gcsUrl?: string;
  isAsset?: boolean;
}

interface ImageAttachmentPreviewProps {
  images: AttachedImage[];
  onRemoveImage: (id: string) => void;
}

export const ImageAttachmentPreview: React.FC<ImageAttachmentPreviewProps> = ({ images, onRemoveImage }) => {
  if (images.length === 0) {
    return null;
  }

  console.log('[ImageAttachmentPreview] images', images);

  const getObjectNameFromUrl = (url: string): string | null => {
    console.log('[ImageAttachmentPreview] getObjectNameFromUrl', { url });

    try {
      const parsed = new URL(url);
      const lastSegment = parsed.pathname.split('/').filter(Boolean).pop();
      console.log('[ImageAttachmentPreview] getObjectNameFromUrl', { lastSegment });
      return lastSegment ? decodeURIComponent(lastSegment) : null;
    } catch {
      return null;
    }
  };

  const looksLikeJobId = (value: string): boolean => /^(image|video)-\d+-[a-z0-9-]+$/i.test(value);

  const getAssetJobId = (image: AttachedImage): string | null => {
    if (image.jobId) {
      return image.jobId;
    }

    if (looksLikeJobId(image.id)) {
      return image.id;
    }

    const url = image.gcsURL || image.gcsUrl;

    if (!url) {
      return null;
    }

    const objectName = 'asset-e9744bf8-243b-4daf-9f42-6d86de6f2b4b.webp'; // getObjectNameFromUrl(url);

    if (!objectName) {
      return null;
    }

    return extractJobIdFromObjectName(objectName);
  };

  return (
    <div className="flex gap-2">
      {images.map((image, index) => (
        <div
          key={image.id}
          className={classNames(
            'relative w-20 h-20 rounded-lg overflow-hidden',
            'bg-dualite-background-tertiary border border-dualite-border-primary',
          )}
        >
          <img src={image.base64} alt={`Attached image ${index + 1}`} className="w-full h-full object-cover" />

          {/* Loading overlay */}
          {image.isAttaching && (
            <div className="absolute inset-0 bg-dualite-background-cta/50 flex items-center justify-center">
              <div className="i-ph:spinner-gap-light text-xl text-dualite-text-primary animate-spin" />
            </div>
          )}

          {/* Remove button */}
          <button
            onClick={async (e) => {
              e.stopPropagation();

              const isAssetType = image.source === 'upload' || image.isAsset === true;
              console.log('[ImageAttachmentPreview] remove clicked', {
                id: image.id,
                source: image.source,
                isAsset: image.isAsset,
                isAssetType,
              });

              if (isAssetType) {
                const jobId = getAssetJobId(image);
                console.log('[ImageAttachmentPreview] resolved asset jobId', { imageId: image.id, jobId });

                if (jobId) {
                  try {
                    // Remove from local pending store (if present)
                    removePendingAssetJobIds([jobId]);
                    console.log('[ImageAttachmentPreview] calling DELETE /api/asset-record', { jobIds: [jobId] });

                    const res = await fetch('/api/asset-record', {
                      method: 'DELETE',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ jobIds: [jobId] }),
                    });

                    let payload: unknown = null;

                    try {
                      payload = await res.json();
                    } catch (err) {
                      // ignore json parse errors
                    }

                    console.log('[ImageAttachmentPreview] /api/asset-record response', {
                      ok: res.ok,
                      status: res.status,
                      payload,
                    });
                  } catch (error) {
                    console.error('[ImageAttachmentPreview] Failed to delete asset record:', error);
                  }
                } else {
                  console.warn('[ImageAttachmentPreview] asset-type image but no jobId found', { image });
                }
              }

              onRemoveImage(image.id);
            }}
            className={classNames(
              'absolute top-1 right-1 w-5 h-5 rounded-full',
              'flex items-center justify-center',
              'bg-dualite-background-tertiary/80 hover:bg-dualite-background-tertiary',
              'border border-dualite-border-primary',
              'transition-colors duration-150',
            )}
          >
            <div className="i-ph:x text-xs text-dualite-text-primary" />
          </button>
        </div>
      ))}
    </div>
  );
};
